/**
 * @file: PresentationTheme.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { ColorMode, ColorModeManager } from '@euler/app/theming/color-mode';
import { useServiceFactory } from '@euler/services/factory';
import * as NavigationBar from 'expo-navigation-bar';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PresentationTheme, ThemeDefinitionByColorMode } from './types';

import { useIsHub } from '@euler/functions/isHub';
import { onErrorIgnore } from '@euler/utils';
import { Platform } from 'react-native';
import defaultTheme from './default';

const kHideNavigationBar = false;

const PredefinedThemes: {
  [colorPalette in PredefinedColorPaletteType]: ThemeDefinitionByColorMode;
} = {
  default: defaultTheme,
};

export type PresentationThemeContextProps = {
  colorMode: ColorMode;
  colorPalette: PredefinedColorPaletteType;
  theme: PresentationTheme;
  setPreferredColorMode: (colorMode: ColorMode) => void;
  setPreferredColorPalette: (palette: PredefinedColorPaletteType) => void;
};

type PredefinedColorPaletteType = 'default';

const kDefaultColorPalette: PredefinedColorPaletteType = 'default';
export const kDefaultPresentationColorMode: ColorMode = 'dark';

const kPreferredColorPalette = 'theme.presentation.preferred_color_palette';

export const PresentationThemeContext =
  createContext<PresentationThemeContextProps>(null as any);

export const usePresentationThemeContext = () =>
  useContext(PresentationThemeContext);

export const usePresentationTheme = () => {
  return usePresentationThemeContext().theme;
};

export const PresentationThemeProvider = memo(
  ({ colorMode, children }: { children?: ReactNode; colorMode: ColorMode }) => {
    const isHub = useIsHub();
    const { defaultStorageService } = useServiceFactory();
    const [colorPalette, setColorPalette] =
      useState<PredefinedColorPaletteType>();

    useEffect(() => {
      defaultStorageService
        .get(kPreferredColorPalette)
        .then(preferredColorPalette => {
          setColorPalette(preferredColorPalette ?? kDefaultColorPalette);
        })
        .catch(error => {
          console.error(error);
          setColorPalette(kDefaultColorPalette);
        });
    }, [defaultStorageService]);

    const setPreferredColorMode = useCallback(
      async (newColorMode: ColorMode) => {
        await ColorModeManager.default.set(newColorMode);
      },
      [],
    );

    const setPreferredColorPalette = useCallback(
      async (newColorPalette: PredefinedColorPaletteType) => {
        setColorPalette(newColorPalette);
        await defaultStorageService.set(
          kPreferredColorPalette,
          newColorPalette,
        );
      },
      [defaultStorageService],
    );

    useEffect(() => {
      if (Platform.OS === 'android' && isHub && kHideNavigationBar) {
        NavigationBar.setVisibilityAsync('hidden').catch(onErrorIgnore);
      }
    }, [isHub]);

    if (colorPalette == null || colorMode == null) {
      return null;
    }

    return (
      <Content
        colorMode={colorMode}
        colorPalette={colorPalette}
        setPreferredColorMode={setPreferredColorMode}
        setPreferredColorPalette={setPreferredColorPalette}
      >
        {children}
      </Content>
    );
  },
);

const Content = memo(
  ({
    colorMode,
    colorPalette,
    setPreferredColorMode,
    setPreferredColorPalette,
    children,
  }: {
    colorMode: ColorMode;
    colorPalette: PredefinedColorPaletteType;
    setPreferredColorMode: (colorMode: ColorMode) => void;
    setPreferredColorPalette: (palette: PredefinedColorPaletteType) => void;
    children?: ReactNode;
  }) => {
    const theme = useMemo<PresentationTheme>(
      () => PredefinedThemes[colorPalette][colorMode],
      [colorMode, colorPalette],
    );
    const value = useMemo<PresentationThemeContextProps>(
      () => ({
        colorMode,
        colorPalette,
        setPreferredColorMode,
        setPreferredColorPalette,
        theme,
      }),
      [
        colorMode,
        colorPalette,
        setPreferredColorMode,
        setPreferredColorPalette,
        theme,
      ],
    );
    return (
      <PresentationThemeContext.Provider value={value}>
        {children}
      </PresentationThemeContext.Provider>
    );
  },
);
